import { AgGridReact } from "ag-grid-react";
import {
  amountCountApi,
  gstCountApi,
  hsnDropdownApi,
  listOfQuotationApi,
  quotationPdfApi,
  totalAmountApi,
} from "Api/Quotation";
import {
  addWorkOrderApi,
  calculateGstApi,
  editWorkOrderApi,
  quotationDropdownApi,
  quotationWorkOrderApi,
  viewWorkOrderApi,
} from "Api/workOrder";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  checkStatusCodeSuccess,
  currentDate,
  DownloadLabel,
  errorHandler,
  NormalDate,
  notFound,
  pdfDownload,
  rupeeSymbol,
  TableErrorMessage,
  TaxType,
} from "Components/constants/Common";
import { QuotationKey, QuotationLabel } from "Components/constants/Quotation";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { WorkOrderKey, WorkOrderLabel } from "Components/constants/WorkOrder";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Row } from "reactstrap";
import * as yup from "yup";
import FormFields from "./FormFields";
import { ColDef } from "ag-grid-community";
import { calculateServiceGstApi } from "Api/service";

const validationSchema = yup.object({
  quoteNo: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.QuoteNo)),
  vendorName: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.VendorName)),
  vendorId: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.VendorId)),
  quoteAmountInc: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.QuoteAmtInc)),
  quoteAmountExl: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.QuoteAmtExl)),
  quoteDate: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.QuotationDate)),
  workOrderAmtInc: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.WorkOrderAmtInc)),
  workOrderAmtExl: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.WorkOrderAmtExl)),
  workOrderDate: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.WorkOrderDate)),
  difference: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.Difference)),
  warranty: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.Warranty)),
  remarks: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.Remarks)),
  gstAmount: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.GstAmount)),
  workOrderNo: yup
    .string()
    .required(validationMessages.required(WorkOrderLabel.WorkOrderNo)),
});

const WorkOrderForm = () => {
  document.title = WorkOrderLabel.WorkOrderTitle;
  const navigate = useNavigate();
  const { workOrderId } = useParams();
  const [quotationOption, setQuotationOption] = useState<Array<any>>([]);
  const [quotationData, setQuotationData] = useState<any>(null);
  const [workOrderData, setWorkOrderData] = useState<any>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [pdfLoader, setPdfLoader] = useState<boolean>(false);
  const [quotation, setQuotation] = useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<Array<any>>([]);
  const [hsnSacOption, setHsnSacOption] = useState<Array<any>>([]);
  const [initialValues, setInitialValue] = useState<boolean>(true);
  const location = useLocation();
  const viewWorkOrderMode = location?.state?.viewWorkOrder;

  const fetchQuotation = () => {
    setLoader(true);
    const payload = {};
    const apiCall = workOrderId
      ? quotationDropdownApi(payload)
      : quotationWorkOrderApi();
    apiCall
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setQuotationOption(
            res?.data?.quotationData?.map((item: any) => ({
              value: item?.id,
              label: item?.quotation_number,
              id: item?.id,
            }))
          );
        } else {
          setQuotationOption([]);
        }
      })
      .catch((err) => {
        setQuotationOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchQuotationData = (id: Number) => {
    setLoader(true);
    const payload = {
      id: id,
    };
    quotationDropdownApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data?.quotationData[0];
          setQuotationData({
            vendorName: data?.customer_name ?? null,
            vendorId: data?.customer_id ?? null,
            quoteDate: data?.quotation_date ?? null,
            quoteAmountExl: res?.data?.financeQuotationItem ?? null,
            quoteAmountInc: res?.data?.includeTaxAmount ?? null,
            discount: data?.total_discount ?? null,
            ...(!quotation && { warranty: data?.warranty ?? null }),
          });
          const quotationProduct = data?.financeQuotationItem?.map(
            (item: any) => {
              return {
                ...item,
                ...(item?.product_id && {
                  product_name: item?.product?.product_code,
                  isService: false,
                  uom: item?.product?.productUom?.uom ?? null,
                }),
                ...(item?.service?.id && {
                  service_id: item?.service?.id,
                  service_name: item?.service?.service_name,
                  isService: true,
                }),
              };
            }
          );
          !workOrderId && setSelectedProducts(quotationProduct ?? []);
        } else {
          setQuotationData(null);
        }
      })
      .catch((err) => {
        setQuotationData(null);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchWorkOrder = (id: number) => {
    setLoader(true);
    viewWorkOrderApi(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setWorkOrderData(res?.data);
          const workOrderProduct = res?.data?.workOrderItem?.map(
            (item: any) => {
              return {
                ...item,
                ...(item?.product_id && {
                  product_name: item?.product?.product_code,
                  isService: false,
                  uom: item?.product?.productUom?.uom ?? null,
                }),
                ...(item?.service_id && {
                  service_name: item?.service?.service_name,
                  isService: true,
                }),
              };
            }
          );
          setSelectedProducts(workOrderProduct);
        } else {
          setWorkOrderData([]);
        }
      })
      .catch((err) => {
        setWorkOrderData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    fetchQuotation();
    if (workOrderId) {
      fetchWorkOrder(Number(workOrderId));
    }
  }, []);

  useEffect(() => {
    if (workOrderId) {
      setQuotation(true);
    } else {
      setQuotation(false);
    }
    setInitialValue(true);
  }, []);

  const fetchHsnSac = () => {
    setLoader(true);
    hsnDropdownApi()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setHsnSacOption(
            res?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.code,
              type: item?.type,
              id: item?.id,
            }))
          );
        } else {
          setHsnSacOption([]);
        }
      })
      .catch((err) => {
        setHsnSacOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    fetchHsnSac();
  }, []);

  const productColumns: ColDef<any>[] = [
    {
      headerName: WorkOrderLabel.SrNo,
      headerTooltip: WorkOrderLabel.SrNo,
      valueGetter: "node.rowIndex + 1",
      cellStyle: { textAlign: "center" },
      editable: false,
      sortable: false,
      filter: false,
      flex: 0.5,
      minWidth: 80,
    },
    {
      headerName: WorkOrderLabel.ProductName,
      headerTooltip: WorkOrderLabel.ProductName,
      field: WorkOrderKey.ProductName,
      tooltipField: WorkOrderKey.ProductName,
      editable: false,
      flex: 1.5,
      minWidth: 150,
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return !params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: QuotationLabel.ServiceName,
      headerTooltip: QuotationLabel.ServiceName,
      field: QuotationKey.ServiceName,
      tooltipField: QuotationKey.ServiceName,
      editable: false,
      flex: 1.5,
      minWidth: 150,
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: WorkOrderLabel.DescriptionOfGoods,
      headerTooltip: WorkOrderLabel.DescriptionOfGoods,
      field: WorkOrderKey.DescriptionOfGoods,
      tooltipField: WorkOrderKey.DescriptionOfGoods,
      cellEditor: "agTextCellEditor",
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: WorkOrderLabel.DescriptionSubHead,
      headerTooltip: WorkOrderLabel.DescriptionSubHead,
      field: WorkOrderKey.DescriptionSubHead,
      tooltipField: WorkOrderKey.DescriptionSubHead,
      cellEditor: "agTextCellEditor",
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: WorkOrderLabel.HSN_SAC,
      headerTooltip: WorkOrderLabel.HSN_SAC,
      field: WorkOrderKey.HSN_SAC,
      tooltipField: WorkOrderKey.HSN_SAC,
      cellEditor: "agSelectCellEditor",
      editable: false,
      cellEditorParams: {
        values: hsnSacOption?.map((option) => option?.label),
        formatValue: (value: any) => {
          const selectedOption = hsnSacOption?.find(
            (opt) => opt?.label === value
          );
          return selectedOption ? selectedOption?.label : value;
        },
      },
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: WorkOrderLabel.Per,
      headerTooltip: WorkOrderLabel.Per,
      field: WorkOrderKey.Per,
      tooltipField: WorkOrderKey.Per,
      editable: false,
      flex: 1.5,
      minWidth: 150,
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return !params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: WorkOrderLabel.Quantity,
      headerTooltip: WorkOrderLabel.Quantity,
      field: WorkOrderKey.Quantity,
      tooltipField: WorkOrderKey.Quantity,
      editable: false,
      valueSetter: (params: any) => {
        const newValue = parseFloat(params.newValue);
        if (!isNaN(newValue) && !params.data.isService) {
          params.data.quantity = newValue;
          countAmount(params.data);
          countGstAmount(params.data);
          setInitialValue(false);
          return true;
        }
        return true;
      },
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: WorkOrderLabel.Rate,
      headerTooltip: WorkOrderLabel.Rate,
      field: WorkOrderKey.Rate,
      tooltipField: WorkOrderKey.Rate,
      editable: !viewWorkOrderMode,
      valueSetter: (params: any) => {
        const newValue = parseFloat(params.newValue);
        if (!isNaN(newValue)) {
          params.data.rate = newValue;
          if (params.data.isService) {
            calculateServiceGst(params.data);
          } else {
            countGstAmount(params.data);
            countAmount(params.data);
          }
          setInitialValue(false);
          return true;
        }
        return false;
      },
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: WorkOrderLabel.GST,
      headerTooltip: WorkOrderLabel.GST,
      field: WorkOrderKey.QuotationGst,
      tooltipField: WorkOrderKey.QuotationGst,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: TaxType?.map((option) => option?.label),
      },
      valueSetter: (params: any) => {
        const newValue = params.newValue;
        const selectedOption = TaxType?.find((opt) => opt?.label === newValue);
        if (selectedOption) {
          params.data.gst = selectedOption.value;
          if (params.data.isService) {
            calculateServiceGst(params.data);
          } else {
            params.data.gst_amount = countGstAmount(params.data);
            params.amount = countAmount(params.data);
          }
          setInitialValue(false);
          return true;
        }
        return false;
      },
      valueGetter: (params: any) => {
        const selectedOption = TaxType?.find(
          (opt) => opt?.value === params.data.gst
        );
        return selectedOption ? selectedOption.label : "";
      },
      editable: false,
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: QuotationLabel.GstAmount,
      headerTooltip: QuotationLabel.GstAmount,
      field: WorkOrderKey.GstAmount,
      tooltipField: WorkOrderKey.GstAmount,
      editable: false,
      cellRenderer: (params: any) => {
        const amount = params.value?.toFixed(2) || 0;
        return rupeeSymbol(amount);
      },
      flex: 2.5,
      minWidth: 150,
    },
    {
      headerName: WorkOrderLabel.Amount,
      headerTooltip: WorkOrderLabel.Amount,
      field: WorkOrderKey.Amount,
      tooltipField: WorkOrderKey.Amount,
      editable: false,
      cellRenderer: (params: any) => {
        const amount = params.value?.toFixed(2) || 0;
        return rupeeSymbol(amount);
      },
      flex: 2.5,
      minWidth: 150,
    },
  ];
  const formInitialValue = {
    quoteNo: workOrderId ? workOrderData?.quotation_id : null,
    vendorName: "",
    vendorId: "",
    quoteAmountInc: "",
    quoteAmountExl: "",
    quoteDate: "",
    workOrderAmtInc: workOrderId
      ? workOrderData?.work_order_incl_tax_amount
      : null,
    workOrderAmtExl: workOrderId
      ? workOrderData?.work_order_exl_tax_amount
      : null,
    workOrderNo: workOrderId ? workOrderData?.work_order_number : null,
    difference: workOrderId ? workOrderData?.diff_amount : null,
    warranty: workOrderId ? workOrderData?.warranty : null,
    workOrderDate: workOrderId
      ? NormalDate(workOrderData?.work_order_date)
      : currentDate,
    woStatus: workOrderId ? workOrderData?.status : null,
    remarks: workOrderId ? workOrderData?.remark : null,
    gst: workOrderId ? workOrderData?.gst : null,
    gstAmount: workOrderId ? workOrderData?.gst_amount : null,
    isGenerated: workOrderId ? workOrderData?.is_tax_invoice_generated : null,
    discount: "",
    product: null,
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValue,
    validationSchema,
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  useEffect(() => {
    if (formik.values.quoteNo) {
      fetchQuotationData(Number(formik.values.quoteNo));
    } else {
      setQuotationData(null);
    }
  }, [formik.values.quoteNo]);

  useEffect(() => {
    if (quotationData) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        ...quotationData,
      }));
    } else {
      formik.setValues((prevValues) => ({
        ...prevValues,
        vendorName: "",
        vendorId: "",
        quoteDate: "",
        quoteAmountExl: "",
        quoteAmountInc: "",
        warranty: "",
        discount: "",
      }));
    }
  }, [quotationData]);

  const handleGstCalculation = () => {
    const payload = {
      gst: formik.values.gst,
      amount: Number(formik.values.workOrderAmtExl),
    };
    const isValidPayload = payload.gst !== null && payload.amount !== null;
    if (isValidPayload) {
      setLoader(true);
      calculateGstApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            formik.setFieldValue("gstAmount", res?.data?.gstAmount);
            formik.setFieldValue("workOrderAmtInc", res?.data?.includingGst);
          }
        })
        .catch((err) => {
          toast.error(err);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const countAmount = (data: any) => {
    const payload = {
      discount_value: data?.discount === "" ? null : data?.discount,
      quantity: data.quantity,
      gst: data.gst,
      rate: data.rate,
    };
    const isValidPayload =
      payload.quantity !== null &&
      payload.gst !== null &&
      payload.rate !== null;
    if (isValidPayload) {
      setLoader(true);
      amountCountApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data;
            setSelectedProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.product_variant_id === data.product_variant_id
                  ? { ...product, with_gst_amount: updatedAmount }
                  : product
              )
            );
          }
        })
        .catch((err) => {
          return err;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const countGstAmount = (data: any) => {
    const payload = {
      quantity: data?.quantity,
      rate: data?.rate,
      gst: data?.gst,
      discount: data?.discount === "" ? null : data?.discount,
    };
    const isValidPayload =
      payload.gst !== null &&
      payload.rate !== null &&
      payload.quantity !== null;

    if (isValidPayload) {
      setLoader(true);
      gstCountApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data;
            setSelectedProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.product_variant_id === data.product_variant_id
                  ? { ...product, gst_amount: updatedAmount }
                  : product
              )
            );
          }
        })
        .catch((err) => {
          setLoader(false);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const calculateServiceGst = (data: any) => {
    const payload = {
      rate: data.rate,
      gst: data.gst,
      discount: formik.values.discount === "" ? null : formik.values.discount,
    };
    const isValidPayload = payload.gst !== null && payload.rate !== null;
    if (isValidPayload) {
      calculateServiceGstApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            setSelectedProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.service?.id === data.service?.id
                  ? {
                      ...product,
                      gst_amount: res?.data?.gstAmount,
                      amount: res?.data?.totalGstAmount,
                    }
                  : product
              )
            );
          }
        })
        .catch((err) => {
          setLoader(false);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const countTotalAmount = (data: any) => {
    setLoader(true);
    const payload = {
      items: data,
    };
    totalAmountApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          formik.setValues((prevValues) => ({
            ...prevValues,
            workOrderAmtExl: res?.data?.totalAmount?.toFixed(2),
            workOrderAmtInc: res?.data?.totalGstAmount?.toFixed(2),
            gstAmount: res?.data?.gstAmount?.toFixed(2),
          }));
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  useEffect(() => {
    const payload = selectedProducts?.map((item) => {
      return {
        gst_amount: item?.gst_amount,
        amount: item?.with_gst_amount,
      };
    });
    if (payload && !initialValues) {
      countTotalAmount(payload);
    }
  }, [
    selectedProducts?.length,
    JSON.stringify(
      selectedProducts?.map((item) => ({
        with_gst_amount: item?.with_gst_amount,
        gst_amount: item?.gst_amount,
      }))
    ),
  ]);

  useEffect(() => {
    handleGstCalculation();
  }, [formik.values.gst, formik.values.workOrderAmtExl]);

  const handleFormSubmit = (values: any) => {
    const quotationProduct = selectedProducts?.map((item) => {
      const productObj = {
        ...(!item?.isService && {
          product_id: item?.product_id,
          product_variant_id: item?.product_variant_id,
        }),
        ...(item?.isService && {
          service_id: item?.service_id,
        }),
        description: item?.description,
        sub_description: item?.sub_description,
        hsn_sac_code: item?.hsn_sac_code,
        gst: item?.gst,
        quantity: item?.quantity,
        rate: item?.rate,
        amount: Number(item?.with_gst_amount) - Number(item?.gst_amount),
        uom: item?.uom,
        gst_amount: Number(item?.gst_amount),
        with_gst_amount: item?.with_gst_amount,
      };

      if (workOrderId && item?.id) {
        return {
          id: item?.id,
          ...productObj,
        };
      }

      return productObj;
    });
    setSubmitLoader(true);
    const payload = {
      quotation_id: values.quoteNo,
      customer_name: values.vendorName,
      customer_id: values.vendorId,
      quotation_exl_tax_amount: Number(values.quoteAmountExl),
      quotation_incl_tax_amount: Number(values.quoteAmountInc),
      quotation_date: values.quoteDate,
      work_order_number: values.workOrderNo,
      work_order_exl_tax_amount: Number(values.workOrderAmtExl),
      work_order_incl_tax_amount: Number(values.workOrderAmtInc),
      work_order_date: values.workOrderDate,
      diff_amount: Number(values.difference),
      warranty: values.warranty,
      remark: values.remarks,
      gst_amount: Number(values.gstAmount),
      workOrderItem: quotationProduct,
    };
    if (workOrderId) {
      editWorkOrderApi(Number(workOrderId), payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/work-order");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setSubmitLoader(false);
        });
    } else {
      addWorkOrderApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/work-order");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setSubmitLoader(false);
        });
    }
  };

  useEffect(() => {
    const difference =
      (formik.values.workOrderAmtExl ?? 0) -
      (Number(formik.values.quoteAmountExl) ?? 0);
    formik.setFieldValue("difference", difference);
  }, [formik.values.quoteAmountExl, formik.values.workOrderAmtExl]);

  const handleDownload = () => {
    const quotation = quotationOption?.find(
      (item) => workOrderData?.quotation_id === item?.value
    )?.label;
    pdfDownload(
      quotationPdfApi,
      workOrderData?.quotation_id,
      `quotation_${quotation}.pdf`,
      setPdfLoader
    );
  };

  return (
    <>
      <div className="page-content">
        {loader && <Loader />}
        <div className="flex-grow-1 mx-3">
          <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
            <span className="fs-4">
              {workOrderId
                ? viewWorkOrderMode
                  ? `${WorkOrderLabel.ViewWorkOrder}-${
                      workOrderData?.work_order_number || ""
                    }`
                  : `${WorkOrderLabel.EditWorkOrder}-${
                      workOrderData?.work_order_number || ""
                    }`
                : WorkOrderLabel.NewWorkOrder}
            </span>
            <div className="d-flex align-items-center gap-2">
              {viewWorkOrderMode && (
                <span className="tooltip-container">
                  <BaseButton
                    className="btn btn-info"
                    loader={pdfLoader}
                    onClick={() => handleDownload()}
                    children={
                      <>
                        <i className="mdi mdi-download-outline" />
                      </>
                    }
                  />
                  <span className="tooltip-text">{DownloadLabel}</span>
                </span>
              )}
              <BaseButton
                className="btn btn-danger"
                onClick={() => navigate("/work-order")}
              >
                <i className="mdi mdi-arrow-left" />
              </BaseButton>
            </div>
          </div>
          <div className="card word-wrap">
            <div className="card-body">
              <FormFields
                formik={formik}
                quotationOption={quotationOption}
                workOrderId={workOrderId}
                viewWorkOrderMode={viewWorkOrderMode}
                setQuotation={setQuotation}
                setInitialValue={setInitialValue}
              />
              <div className="table-css">
                <div className="ag-theme-quartz">
                  <AgGridReact
                    rowData={selectedProducts}
                    columnDefs={productColumns}
                    defaultColDef={{
                      sortable: true,
                      resizable: true,
                      tooltipComponentParams: {
                        color: "blue",
                      },
                    }}
                    domLayout="normal"
                    getRowHeight={() => 35}
                    overlayNoRowsTemplate={notFound.dataNotFound}
                  />
                </div>
              </div>
              {!viewWorkOrderMode && (
                <>
                  <div className="row mt-3 d-flex justify-content-end">
                    <div className="col-auto d-flex justify-content-end gap-2 text-end">
                      <BaseButton
                        type="button"
                        label={workOrderId ? updateLabel : submitLabel}
                        color="success"
                        loader={submitLoader}
                        disable={submitLoader}
                        onClick={() => formik.handleSubmit()}
                      />
                      <BaseButton
                        type="button"
                        label={cancelLabel}
                        onClick={() => navigate("/work-order")}
                        color="danger"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkOrderForm;
